const FULL_SIZE = 512;
const THUMBNAIL_SIZE = 128;
const THUMBNAIL_WEBP_QUALITY = 0.8;
const MAX_SOURCE_BYTES = 5 * 1024 * 1024;

export const UNREAL_ITEM_ICON_CONTENT_TYPE = 'image/png';

export function isUnrealCompatibleItemIcon(contentType: string | null | undefined): boolean {
  return contentType?.trim().toLowerCase() === UNREAL_ITEM_ICON_CONTENT_TYPE;
}

export interface OptimizedItemIcon {
  icon: File;
  thumbnail: File;
  originalName: string;
  originalBytes: number;
  originalWidth: number;
  originalHeight: number;
  optimizedBytes: number;
  thumbnailBytes: number;
  outputType: string;
  hasTransparency: boolean;
}

type DrawableImage = ImageBitmap | HTMLImageElement;

function fileStem(filename: string): string {
  return filename.replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9._-]+/g, '-').replace(/^-+|-+$/g, '') || 'item-icon';
}

async function loadDrawable(file: File): Promise<{ drawable: DrawableImage; width: number; height: number; close: () => void }> {
  if ('createImageBitmap' in window) {
    const bitmap = await createImageBitmap(file);
    return {
      drawable: bitmap,
      width: bitmap.width,
      height: bitmap.height,
      close: () => bitmap.close(),
    };
  }

  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.decoding = 'async';
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error('Das Bild konnte nicht gelesen werden.'));
      image.src = url;
    });
    return {
      drawable: image,
      width: image.naturalWidth,
      height: image.naturalHeight,
      close: () => URL.revokeObjectURL(url),
    };
  } catch (error) {
    URL.revokeObjectURL(url);
    throw error;
  }
}

function createCanvas(size: number, drawable: DrawableImage, sourceWidth: number, sourceHeight: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;

  const context = canvas.getContext('2d', { alpha: true });
  if (!context) throw new Error('Der Browser konnte die Bildoptimierung nicht initialisieren.');

  context.clearRect(0, 0, size, size);
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';

  const scale = Math.min(size / sourceWidth, size / sourceHeight);
  const width = Math.max(1, Math.round(sourceWidth * scale));
  const height = Math.max(1, Math.round(sourceHeight * scale));
  const x = Math.floor((size - width) / 2);
  const y = Math.floor((size - height) / 2);

  context.drawImage(drawable, x, y, width, height);
  return canvas;
}

function canvasBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

async function encodePng(canvas: HTMLCanvasElement): Promise<{ blob: Blob; extension: 'png' }> {
  const png = await canvasBlob(canvas, UNREAL_ITEM_ICON_CONTENT_TYPE);
  if (!png || !isUnrealCompatibleItemIcon(png.type) || png.size === 0) {
    throw new Error('Das Unreal-kompatible PNG-Icon konnte nicht erzeugt werden.');
  }
  return { blob: png, extension: 'png' };
}

async function encodeThumbnail(canvas: HTMLCanvasElement, quality: number): Promise<{ blob: Blob; extension: 'webp' | 'png' }> {
  const webp = await canvasBlob(canvas, 'image/webp', quality);
  if (webp && webp.type === 'image/webp' && webp.size > 0) return { blob: webp, extension: 'webp' };

  const png = await canvasBlob(canvas, 'image/png');
  if (!png || png.size === 0) throw new Error('Das optimierte Listen-Thumbnail konnte nicht erzeugt werden.');
  return { blob: png, extension: 'png' };
}

function canvasHasTransparency(canvas: HTMLCanvasElement): boolean {
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return false;
  const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
  for (let index = 3; index < pixels.length; index += 16) {
    if (pixels[index] < 255) return true;
  }
  return false;
}

export async function optimizeItemIcon(file: File): Promise<OptimizedItemIcon> {
  if (!['image/png', 'image/webp'].includes(file.type)) {
    throw new Error('Es sind nur PNG- und WebP-Icons erlaubt.');
  }
  if (file.size <= 0 || file.size > MAX_SOURCE_BYTES) {
    throw new Error('Das Ausgangsbild darf maximal 5 MB groß sein.');
  }

  const loaded = await loadDrawable(file);
  try {
    if (!loaded.width || !loaded.height) throw new Error('Das Bild besitzt ungültige Abmessungen.');
    if (loaded.width > 8192 || loaded.height > 8192) {
      throw new Error('Das Ausgangsbild darf höchstens 8192 × 8192 Pixel groß sein.');
    }

    const fullCanvas = createCanvas(FULL_SIZE, loaded.drawable, loaded.width, loaded.height);
    const thumbnailCanvas = createCanvas(THUMBNAIL_SIZE, loaded.drawable, loaded.width, loaded.height);
    const hasTransparency = canvasHasTransparency(fullCanvas);

    const [full, thumbnail] = await Promise.all([
      encodePng(fullCanvas),
      encodeThumbnail(thumbnailCanvas, THUMBNAIL_WEBP_QUALITY),
    ]);

    const stem = fileStem(file.name);
    const icon = new File([full.blob], `${stem}-512.${full.extension}`, {
      type: full.blob.type,
      lastModified: Date.now(),
    });
    const thumbnailFile = new File([thumbnail.blob], `${stem}-128.${thumbnail.extension}`, {
      type: thumbnail.blob.type,
      lastModified: Date.now(),
    });

    return {
      icon,
      thumbnail: thumbnailFile,
      originalName: file.name,
      originalBytes: file.size,
      originalWidth: loaded.width,
      originalHeight: loaded.height,
      optimizedBytes: icon.size,
      thumbnailBytes: thumbnailFile.size,
      outputType: icon.type,
      hasTransparency,
    };
  } finally {
    loaded.close();
  }
}
